import { useId } from 'react';
import type {
  LotesDeProdutoNoResumo,
  ResumoDaSincronizacao as Resumo,
} from '../../api/sincronizacao.js';
import type { Consulta } from '../../api/useConsulta.js';
import { CartaoIndicador } from '../../componentes/CartaoIndicador.js';
import type { Indicador } from '../../componentes/CartaoIndicador.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { FalhaDeCarregamento } from '../../componentes/FalhaDeCarregamento.js';

const NUMERO = new Intl.NumberFormat('pt-BR');

function n(valor: number): string {
  return NUMERO.format(valor);
}

/**
 * Lotes de PRODUTO (`saldo_lote`): o total grande é o dos ativos, como o
 * coletor os vê. Middleware antigo não manda a contagem — o cartão diz isso
 * em vez de mostrar zero, que afirmaria um armazém sem lote.
 */
function indicadorDosLotesDeProduto(lotes: LotesDeProdutoNoResumo | undefined): Indicador {
  if (lotes === undefined) {
    return {
      rotulo: 'Lotes de produto',
      valor: '—',
      tom: 'neutro',
      nota: 'O servidor ainda não conta os lotes de produto',
      tomNota: 'neutro',
    };
  }

  return {
    rotulo: 'Lotes de produto',
    valor: n(lotes.ativos),
    tom: 'neutro',
    nota: `${n(lotes.ativos)} ativos · ${n(lotes.foraDeUso)} fora de uso`,
    tomNota: 'neutro',
  };
}

/**
 * Os totais do que a sincronização gravou, cada um com a sua quebra. Nada
 * aqui é calculado na tela: o servidor conta, a tela escreve. "Fora de uso"
 * é `ativo = false` — excluído no ERP ou inativo lá (total − ativos).
 */
function indicadoresDoResumo(resumo: Resumo): readonly Indicador[] {
  const { categorias, produtos, saldos, lotes, descartados, lotesDeProduto } = resumo;

  return [
    {
      rotulo: 'Categorias',
      valor: n(categorias.total),
      tom: 'neutro',
      nota: `${n(categorias.ativas)} ativas · ${n(categorias.excluidas)} fora de uso`,
      tomNota: 'neutro',
    },
    {
      rotulo: 'Produtos',
      valor: n(produtos.total),
      tom: 'neutro',
      nota: `${n(produtos.ativos)} ativos · ${n(produtos.excluidos)} fora de uso`,
      tomNota: 'neutro',
    },
    {
      rotulo: 'Saldos',
      valor: n(saldos.total),
      tom: 'neutro',
      nota: `${n(saldos.ativos)} ativos · ${n(saldos.comDivergenciaPendente)} com divergência pendente`,
      tomNota: saldos.comDivergenciaPendente > 0 ? 'atencao' : 'neutro',
    },
    indicadorDosLotesDeProduto(lotesDeProduto),
    {
      // Lotes da EXPORTAÇÃO do ERP (os pacotes de registros que o Senior
      // manda), não lotes de produto.
      rotulo: 'Lotes do ERP',
      valor: n(lotes.recebidos),
      tom: 'neutro',
      nota: `${n(lotes.confirmados)} confirmados · último em ${formatarDataHora(lotes.ultimoRecebidoEm, 'nunca')}`,
      tomNota: 'neutro',
    },
    {
      rotulo: 'Descartados',
      valor: n(descartados),
      tom: descartados > 0 ? 'atencao' : 'neutro',
      nota: descartados > 0 ? 'Registros do ERP que não entraram' : 'Nada recusado',
      tomNota: descartados > 0 ? 'atencao' : 'neutro',
    },
  ];
}

/** A faixa de indicadores do topo, com os desfechos da consulta. */
export function ResumoDaSincronizacao({ consulta }: { readonly consulta: Consulta<Resumo> }) {
  const idTitulo = useId();

  return (
    <section className="sincronizacao__resumo" aria-labelledby={idTitulo}>
      <h2 id={idTitulo} className="apenas-leitor-de-tela">
        Resumo da sincronização
      </h2>
      {consulta.erro !== null ? (
        <FalhaDeCarregamento mensagem={consulta.erro} aoTentarDeNovo={consulta.recarregar} />
      ) : consulta.dados === null ? (
        <p className="sincronizacao__discreto" role="status">
          Carregando o resumo…
        </p>
      ) : (
        <div className="sincronizacao__indicadores">
          {indicadoresDoResumo(consulta.dados).map((indicador) => (
            <CartaoIndicador key={indicador.rotulo} indicador={indicador} />
          ))}
        </div>
      )}
    </section>
  );
}
